// Supported languages configuration
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: 'GB', dir: 'ltr' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: 'UA', dir: 'ltr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: 'RU', dir: 'ltr' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: 'TH', dir: 'ltr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: 'DE', dir: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: 'FR', dir: 'ltr' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: 'CN', dir: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: 'ES', dir: 'ltr' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', flag: 'IL', dir: 'rtl' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: 'NL', dir: 'ltr' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: 'IT', dir: 'ltr' }
];

export const DEFAULT_LANGUAGE = 'en';
export const STORAGE_KEY = 'phanganTaxiLang';

/**
 * Detects browser language and returns supported language code
 * @returns {string} Language code (e.g., 'en', 'ru', 'th')
 */
export const detectBrowserLanguage = () => {
  try {
    const browserLang = navigator.language || navigator.userLanguage;
    const langCode = browserLang?.split('-')?.[0]?.toLowerCase();
    
    // Check if detected language is supported
    const isSupported = SUPPORTED_LANGUAGES?.some(lang => lang?.code === langCode);
    
    return isSupported ? langCode : DEFAULT_LANGUAGE;
  } catch (error) {
    console.error('Error detecting browser language:', error);
    return DEFAULT_LANGUAGE;
  }
};

/**
 * Gets the current language based on priority:
 * 1. Stored preference in localStorage
 * 2. Browser language (if supported)
 * 3. Default language (EN)
 * @returns {string} Language code
 */
export const getCurrentLanguage = () => {
  try {
    // Check localStorage first (user's explicit choice)
    const storedLang = localStorage.getItem(STORAGE_KEY);
    if (storedLang && SUPPORTED_LANGUAGES?.some(lang => lang?.code === storedLang)) {
      return storedLang;
    }
    
    // If no stored preference, detect browser language
    return detectBrowserLanguage();
  } catch (error) {
    console.error('Error getting current language:', error);
    return DEFAULT_LANGUAGE;
  }
};

/**
 * Sets the language preference in localStorage
 * @param {string} langCode - Language code to set
 */
export const setLanguage = (langCode) => {
  try {
    if (SUPPORTED_LANGUAGES?.some(lang => lang?.code === langCode)) {
      localStorage.setItem(STORAGE_KEY, langCode);
      
      // Update document direction for RTL languages
      const language = SUPPORTED_LANGUAGES?.find(lang => lang?.code === langCode);
      document.documentElement.dir = language?.dir || 'ltr';
      document.documentElement.lang = langCode;
      
      // Trigger storage event for other components
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new CustomEvent('languagechange', { detail: { language: langCode } }));
      
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error setting language:', error);
    return false;
  }
};

/**
 * Gets language details by code
 * @param {string} langCode - Language code
 * @returns {object} Language configuration object
 */
export const getLanguageDetails = (langCode) => {
  return SUPPORTED_LANGUAGES?.find(lang => lang?.code === langCode) || SUPPORTED_LANGUAGES?.[0];
};

/**
 * Checks if a language uses RTL (right-to-left) direction
 * @param {string} langCode - Language code
 * @returns {boolean} True if RTL, false otherwise
 */
export const isRTL = (langCode) => {
  const language = getLanguageDetails(langCode);
  return language?.dir === 'rtl';
};

/**
 * Gets the BCP 47 locale tag for HTML date input
 * Maps language codes to proper locale tags for date formatting
 * @param {string} langCode - Language code
 * @returns {string} BCP 47 locale tag (e.g., 'en-US', 'ru-RU', 'th-TH')
 */
export const getDateInputLocale = (langCode) => {
  const localeMap = {
    en: 'en-US',
    uk: 'uk-UA',
    ru: 'ru-RU', 
    th: 'th-TH',
    de: 'de-DE',
    fr: 'fr-FR',
    zh: 'zh-CN',
    es: 'es-ES',
    it: 'it-IT',
    nl: 'nl-NL',
    he: 'he-IL'
  };
  
  return localeMap?.[langCode] || localeMap?.en;
};
function getDateFnsLocale(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getDateFnsLocale is not implemented yet.', args);
  return null;
}

export { getDateFnsLocale };